import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LuGripVertical } from "react-icons/lu";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toastManager } from "@/components/ui/toast";
import { pageIdsAfterMove } from "../lib/review-helpers";
import { pageFileUrl, type ReviewPage, type ReviewScriptDetail } from "../lib/review-client";
import { useReorderPages } from "../hooks/use-review";

const prefersReducedMotion =
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function ReviewThumbRail({
  scriptId,
  pages,
  selectedPageId,
  onSelect,
  layout,
  selectMode = false,
  selectedIds,
  onToggleSelect,
}: {
  scriptId: string;
  pages: ReviewPage[];
  selectedPageId: string | null;
  onSelect: (pageId: string) => void;
  layout: "grid" | "list";
  selectMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (pageId: string) => void;
}) {
  const queryClient = useQueryClient();
  const reorder = useReorderPages(scriptId);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  function onDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const nextIds = pageIdsAfterMove(pages, String(active.id), String(over.id));
    if (!nextIds) return;

    const previous = queryClient.getQueryData<ReviewScriptDetail>(["review", "scripts", scriptId]);
    if (previous) {
      const byId = new Map(previous.pages.map(p => [p.id, p]));
      const nextPages = nextIds.map(id => byId.get(id)).filter((p): p is ReviewPage => !!p);
      queryClient.setQueryData(["review", "scripts", scriptId], { ...previous, pages: nextPages });
    }

    reorder.mutate(nextIds, {
      onError: () => {
        if (previous) queryClient.setQueryData(["review", "scripts", scriptId], previous);
        toastManager.add({ type: "error", title: "Couldn't reorder pages" });
      },
    });
  }

  const railClass = cn(layout === "grid" ? "grid grid-cols-3 gap-2" : "flex flex-col gap-2");

  if (selectMode) {
    return (
      <div className={railClass}>
        {pages.map((page, i) => (
          <SelectThumb
            key={page.id}
            page={page}
            index={i}
            selected={page.id === selectedPageId}
            multiSelected={selectedIds?.has(page.id) ?? false}
            onSelect={() => onSelect(page.id)}
            onToggleSelect={() => onToggleSelect?.(page.id)}
          />
        ))}
      </div>
    );
  }

  const activePage = pages.find(p => p.id === activeId);
  const activeIndex = activePage ? pages.indexOf(activePage) : -1;

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <SortableContext items={pages.map(p => p.id)} strategy={layout === "grid" ? rectSortingStrategy : verticalListSortingStrategy}>
        <div className={railClass}>
          {pages.map((page, i) => (
            <Thumb
              key={page.id}
              page={page}
              index={i}
              selected={page.id === selectedPageId}
              disabled={reorder.isPending}
              onSelect={() => onSelect(page.id)}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={prefersReducedMotion ? null : undefined}>
        {activePage ? <ThumbVisual page={activePage} index={activeIndex} selected={false} dragging /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function SelectThumb({
  page,
  index,
  selected,
  multiSelected,
  onSelect,
  onToggleSelect,
}: {
  page: ReviewPage;
  index: number;
  selected: boolean;
  multiSelected: boolean;
  onSelect: () => void;
  onToggleSelect: () => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "relative block w-full overflow-hidden rounded-md ring-1 ring-foreground/10",
          selected && "ring-2 ring-primary",
          multiSelected && "outline outline-2 outline-offset-2 outline-sky-500",
        )}
      >
        <ThumbVisual page={page} index={index} selected={selected} />
      </button>
      <div className="absolute right-1 top-1 z-10" onClick={e => e.stopPropagation()}>
        <Checkbox
          checked={multiSelected}
          onCheckedChange={() => onToggleSelect()}
          aria-label={`Select page ${index + 1}`}
        />
      </div>
    </div>
  );
}

function Thumb({
  page,
  index,
  selected,
  disabled,
  onSelect,
}: {
  page: ReviewPage;
  index: number;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className={cn("relative", isDragging && "opacity-40")}>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "relative block w-full overflow-hidden rounded-md ring-1 ring-foreground/10",
          selected && "ring-2 ring-primary",
        )}
      >
        <ThumbVisual page={page} index={index} selected={selected} />
      </button>
      <button
        type="button"
        aria-label={`Reorder page ${index + 1}`}
        disabled={disabled}
        className="absolute right-1 top-1 flex size-6 items-center justify-center rounded bg-black/50 text-white disabled:opacity-40"
        {...attributes}
        {...listeners}
      >
        <LuGripVertical className="size-3.5" />
      </button>
    </div>
  );
}

function ThumbVisual({
  page,
  index,
  selected,
  dragging,
}: {
  page: ReviewPage;
  index: number;
  selected: boolean;
  dragging?: boolean;
}) {
  return (
    <div className={cn("relative overflow-hidden rounded-md", dragging && "shadow-lg ring-2 ring-primary")}>
      <img
        src={pageFileUrl(page.id, page.sha256)}
        alt={`Page ${index + 1}`}
        className="aspect-3/4 w-full object-cover"
        style={page.rotation ? { transform: `rotate(${page.rotation}deg)` } : undefined}
      />
      <Badge variant={selected ? "default" : "secondary"} className="absolute left-1 top-1">
        {index + 1}
      </Badge>
      {page.flags.length > 0 && (
        <Badge variant="secondary" className="absolute bottom-1 left-1">
          {page.flags[0]}
          {page.flags.length > 1 ? ` +${page.flags.length - 1}` : ""}
        </Badge>
      )}
    </div>
  );
}
